import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { StaticPageLayout } from '@/components/layout/StaticPageLayout';

interface FAQItemProps {
  question: string;
  answer: string;
  /** Optional link appended after the answer text (e.g. a details page). */
  link?: { to: string; label: string };
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, link, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span className="font-medium text-gray-900 dark:text-white pr-4">
          {question}
        </span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 bg-gray-50 dark:bg-gray-800/50">
          <p className="text-gray-600 dark:text-gray-400">
            {answer}
            {link && (
              <>
                {' '}
                <Link
                  to={link.to}
                  className="text-orange-600 dark:text-orange-400 hover:underline"
                >
                  {link.label}
                </Link>
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

type FAQKey =
  | 'free'
  | 'proScans'
  | 'howItWorks'
  | 'currenciesConversion'
  | 'offline'
  | 'import'
  | 'voice'
  | 'privacy'
  | 'groupSize'
  | 'deleteAccount'
  | 'payments';

const FAQ_KEYS: FAQKey[] = [
  'free',
  'proScans',
  'howItWorks',
  'currenciesConversion',
  'offline',
  'import',
  'voice',
  'privacy',
  'groupSize',
  'deleteAccount',
  'payments',
];

const FAQ_LINKS: Partial<Record<FAQKey, { to: string; label: string }>> = {
  deleteAccount: {
    to: '/account/deletion',
    label: 'splitfiesta.com/account/deletion',
  },
};

export function FAQPage() {
  const { t } = useTranslation();
  const pagesLoaded = useNamespace('pages');
  // undefined = untouched, so the first entry opens by default; null = the
  // user closed everything.
  const [toggledKey, setToggledKey] = useState<FAQKey | null | undefined>(
    undefined
  );

  if (!pagesLoaded) {
    return null;
  }

  const openKey = toggledKey === undefined ? FAQ_KEYS[0] : toggledKey;

  return (
    <StaticPageLayout
      title={t('pages:faq.title')}
      subtitle={t('pages:faq.subtitle')}
    >
      <div className="space-y-8">
        {/* FAQ List */}
        <div className="space-y-3">
          {FAQ_KEYS.map(key => (
            <FAQItem
              key={key}
              question={t(`pages:faq.items.${key}.question`)}
              answer={t(`pages:faq.items.${key}.answer`)}
              link={FAQ_LINKS[key]}
              isOpen={openKey === key}
              onToggle={() => setToggledKey(openKey === key ? null : key)}
            />
          ))}
        </div>

        {/* Still have questions */}
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('pages:faq.moreQuestions.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('pages:faq.moreQuestions.description')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            {t('pages:faq.moreQuestions.button')}
          </Link>
        </div>
      </div>
    </StaticPageLayout>
  );
}

export default FAQPage;
